import type { ChangeEvent } from 'react'
import React, { useEffect, useState } from 'react'
import { Alert, Text, useToast, csx } from '@vtex/admin-ui'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'

import type { DrawerProps } from '../../../../../../ui/components/Drawer'
import { Drawer, DrawerBody } from '../../../../../../ui/components/Drawer'
import type { CheckoutProductListItemProps } from '../../../../components'
import { StepSubmitButton, ProductImage } from '../../../../components'
import { EditCartItemDrawerCloseButton } from './EditCartItemDrawerCloseButton'
import type {
  Message,
  OrderForm,
} from '../../../../../../core/requests/checkout'
import { resizeItemImageResolution } from '../../../../../../core/utils/orderForm'
import { getAttachmentItems } from '../../../../utils'
import { useVariations } from '../../../../hooks'
import { getFormattedPrice } from '../../../../../../core/utils/orderFormItem'
import { RemoveItemButton } from './RemoveItemButton'
import { useIsMounted } from '../../../../../../core/hooks/useIsMounted'
import { useCheckout } from '../../../../state'
import { NumberInput } from './NumberInput'

export type EditCardActionProps = {
  itemIndex: number
  editionDisabled?: boolean
  updateCartItem: (
    itemId: string,
    itemIndex: number,
    simulationQuantity: number,
    sellerId: string,
    inHands?: boolean
  ) => Promise<OrderForm>
  removeCartItem: (
    itemId: string,
    itemIndex: number,
    sellerId: string
  ) => Promise<OrderForm>
}

export type EditCartItemDrawerProps = CheckoutProductListItemProps &
  DrawerProps &
  EditCardActionProps

const checkItemQuantityIsAvailable = (messages: Message[] | undefined) => {
  return !messages?.find(
    (message) => message.code === 'itemQuantityNotAvailable'
  )
}

export const EditCartItemDrawer = ({
  item,
  itemIndex,
  messages,
  currencyCode,
  isOpen,
  editionDisabled,
  updateCartItem,
  removeCartItem,
  onClose,
  ...props
}: EditCartItemDrawerProps) => {
  const [isBusy, setIsBusy] = useState(false)
  const [simulationQuantity, setSimulationQuantity] = useState(item.quantity)
  const formattedPrice = getFormattedPrice(item, simulationQuantity)
  const [itemMessages, setItemMessages] = useState(messages)
  const checkout = useCheckout()

  const { variations } = useVariations(item)

  const attachments = getAttachmentItems(item.attachments)
  const showToast = useToast()
  const { formatMessage } = useIntl()
  const isMounted = useIsMounted()
  const isItemQuantityAvailable = checkItemQuantityIsAvailable(itemMessages)

  const handleNumberOfItemsChange = (value: number) => {
    if (itemMessages?.length) {
      setItemMessages([])
    }

    setSimulationQuantity(value)
  }

  const handleDrawerClose = () => {
    onClose()

    if (itemMessages?.length) {
      setItemMessages([])
    }
  }

  const handleItemSubmit = async (event: ChangeEvent<HTMLFormElement>) => {
    event.preventDefault()

    // Here the items quantity was already set by the orderform api, so no need to request it again
    if (!isItemQuantityAvailable) {
      handleDrawerClose()

      return
    }

    setIsBusy(true)

    try {
      if (itemMessages?.length) {
        setItemMessages([])
      }

      const sellerId = item.seller ?? '1'

      const response = await updateCartItem(
        item.id,
        itemIndex,
        simulationQuantity,
        sellerId
      )

      if (!response.messages.length) {
        handleDrawerClose()

        return
      }

      setItemMessages(response.messages)

      const newOrderFormItem = response.items.find(({ id }) => id === item.id)

      if (newOrderFormItem) {
        setSimulationQuantity(newOrderFormItem.quantity)
        checkout.removeMessageByCode('itemQuantityNotAvailable')
      }
    } catch {
      showToast({
        key: 'client-profile-data-error',
        variant: 'critical',
        dismissible: true,
        message: formatMessage({
          id: 'checkout.edit-cart-item-drawer.confirm.error',
        }),
      })
    } finally {
      if (isMounted.current) {
        setIsBusy(false)
      }
    }
  }

  const handleRemoveItem = async () => {
    setIsBusy(true)

    try {
      await removeCartItem(item.id, itemIndex, item.seller)

      onClose()
    } catch {
      showToast({
        key: 'client-profile-data-error',
        variant: 'critical',
        dismissible: true,
        message: formatMessage({
          id: 'checkout.remove-cart-item-drawer.confirm.error',
        }),
      })
    } finally {
      if (isMounted.current) {
        setIsBusy(false)
      }
    }
  }

  useEffect(() => {
    if (isOpen) {
      setSimulationQuantity(item.quantity)
    }
  }, [item.quantity, isOpen])

  return (
    <Drawer
      {...props}
      className={csx({
        padding: '$space-0 !important',
      })}
      isOpen={isOpen}
      onClose={handleDrawerClose}
    >
      <form onSubmit={handleItemSubmit}>
        <div
          className={csx({
            alignItems: 'center',
            flexDirection: 'column',
            display: 'flex',
          })}
        >
          <ProductImage
            src={resizeItemImageResolution(item.imageUrl)}
            alt={item.name}
            sizeInPx={308}
            className={csx({
              width: '200px',
              overflow: 'none',
              '@desktop': {
                width: '308px',
              },
            })}
          />
          <EditCartItemDrawerCloseButton onClick={handleDrawerClose} />
        </div>
        <DrawerBody className={csx({ padding: '$space-6' })}>
          <Text
            variant="title1"
            className={csx({ fontSize: '1.25rem', display: 'block' })}
          >
            {item.name}
          </Text>
          <Text tone="secondary" variant="title2">
            <FormattedNumber
              value={formattedPrice}
              style="currency"
              currency={currencyCode}
            />
          </Text>
          <div className={csx({ marginTop: '$space-4' })}>
            {variations.map((skuVariation) => (
              <Text variant="title2" key={skuVariation}>
                {skuVariation}
              </Text>
            ))}
            {attachments.map((attachment) => {
              return (
                attachment.value && (
                  <Text
                    variant="title2"
                    aria-label={`itemAttachment-${attachment.name}`}
                    key={`${attachment.name}-${attachment.value}`}
                  >
                    {`${attachment.name}: ${attachment.value}`}
                  </Text>
                )
              )
            })}
          </div>
          <div className={csx({ marginTop: '$space-5', display: 'flex' })}>
            <RemoveItemButton disabled={isBusy} onClick={handleRemoveItem} />
            <NumberInput
              value={simulationQuantity}
              disabled={editionDisabled}
              onChange={handleNumberOfItemsChange}
            />
          </div>
          {!isItemQuantityAvailable && (
            <Alert variant="warning" className={csx({ marginTop: '$space-2' })}>
              <FormattedMessage
                id="checkout.edt-cart-item-drawer.max-itens-error"
                values={{ actualQty: item.quantity }}
              />
            </Alert>
          )}
          <StepSubmitButton
            type="submit"
            loading={isBusy}
            className={csx({ marginTop: '$space-6', width: 'full' })}
            disabled={
              (isBusy || item.quantity === simulationQuantity) &&
              isItemQuantityAvailable
            }
          >
            <FormattedMessage id="confirm" />
          </StepSubmitButton>
        </DrawerBody>
      </form>
    </Drawer>
  )
}
